import { Inject, Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';

import { CONFIG, ConfigService, FrontendConfig } from '@fry/lib/config';
import { AuthService, AuthToken, AuthErrorNotAuthenticated } from '@fry/lib/auth';
import { LoggerService } from '@fry/lib/utils';
import { SentryService } from '@fry/lib/error';

import { environment } from '../../environments/environment';
import { ProfileStore } from '../profile';
import { OidcService } from '@fry-it/risr-core';
import { ClusterService } from '@fry-it/risr-core';

// 1. Load configuration from the server (for API)
// 2. Get AuthToken and handle the path org/org_ components
// 3. Attempt authenticate using OIDC
//    When authenticated
// 4.   Load cluster configuration
// 5.   Setup sentry
// 6.   Run Angular bootstrap if we're authenticated


@Injectable()
export class AppLoadService {
  private initialized = false;

  constructor(
    private auth: AuthService,
    private authToken: AuthToken,
    private cluster: ClusterService,
    private config: ConfigService,
    private logger: LoggerService,
    private oidc: OidcService,
    private sentry: SentryService,
    private profile: ProfileStore,
    @Inject(CONFIG) private configFrontend: FrontendConfig
  ) { }

  async superInitializeApp(): Promise<void> {
    console.info('SuperInitializeApp:: start init process');
    try {
        // Load server and frontend configuration
        const configuration =
          await lastValueFrom(this.config.prepare(this.configFrontend, localStorage));
        if (!configuration) {
          throw new Error($localize `Unable to load application configuration!`);
        }

        let searchstring = window.location.search;
        if (searchstring && searchstring[0] === '?') {
          searchstring = searchstring.substring(1);
        }
        if (searchstring) {
          const params = new HttpParams({fromString: searchstring});
          const org = params.get('org');
          if (org) {
            this.authToken.organisation = `org_${org}`;
          }
        }
        const basePath = location.href.replace(document.baseURI, '/')
        if (basePath.startsWith('/org/')) {
          const path = basePath.split('/');
          const org = path[2];
          let fast = false;
          let toRemove = 2;
          if (path[3] === 'fast') {
            fast = true;
            toRemove = 3;
          }

          sessionStorage.setItem('fastRedirect', fast.toString());
          const rest = ['', ...path.slice(toRemove+1)].join('/');
          console.log('Redirecting to', rest);

          if (org) {
            this.authToken.organisation = `org_${org}`;
            this.oidc.setOrganisation(`org_${org}`);
            // this.location.replaceState(document.baseURI.replace(window.location.origin, ''));
            window.location.href = document.baseURI.replace(/\/$/, '') + rest;
            throw new AuthErrorNotAuthenticated($localize `Setting organisation, redirecting...`);
          }
        }

        // Sentry configuration
        const sentryConfig = this.config.server.sentry;
        if (sentryConfig) {
          sentryConfig.release = environment.version.version;
          sentryConfig.tracingOrigins = Object.values(this.config.server.urls);
          this.sentry.init(sentryConfig);
          this.sentry.watchUser(this.auth.currentUser());
          if (!this.sentry.isEnabled) {
            console.warn('Sentry is configured but explicitly disabled!');
          }
        } else {
          console.warn('Sentry is not configured and will be disabled!');
        }

        this.logger.init();

        console.info('SuperInitializeApp:: done process');
    } catch (error) {
        console.info('SuperInitializeApp:: failed process');
        throw error;
    }
  }

  async initializeApp(): Promise<void> {
    console.info('InitializeApp:: start init process');
    if (this.initialized) {
      return;
    }
    try {
        // Check if we're logged in and if not we throw AuthError to stop
        // initialisation.
        if (!await this.oidc.init({ relogin: true })) {
          throw new AuthErrorNotAuthenticated($localize `User not authenticated, initiated auth...`);
        }

        // Load all information about the User's cluster
        await this.cluster.loadProfileOrganisations();
        this.auth.updateCurrentUser();

        this.auth.subscribeUserChange();

        this.profile.init();

        sessionStorage.removeItem('fastRedirect');

        console.info('InitializeApp:: done process');
    } catch (error) {
        console.info('InitializeApp:: failed process');
        throw error;
    }

    this.initialized = true;
  }
}
